import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaterialModal from "./MaterialModal";
import Textfield from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { notifyGreen } from "../../actions/notifyActions";
import { hideModal } from "../../actions/actionCreators";
import withModal from "./withModal";
import h from "../../helpers";

import { IAppState } from "../../types/stores";
import { IUserCompany } from "../../types/stores/adminCompanies";
import { IAutomationItemWithName } from "../../types/stores/manageAutomation";

interface ITestAutomationModal {
    isOpen: boolean;
    modalProps: {
        companyIdFilter: number;
        items: Array<IAutomationItemWithName>;
    };
}

const TestAutomationModal: React.FC<ITestAutomationModal> = ({ isOpen, modalProps }: ITestAutomationModal) => {
    const dispatch = useDispatch();

    // State
    const [selectedValue, setSelectedValue] = useState(5);
    const [table, setTable] = useState("");
    const [subType, setSubType] = useState<number | null>(null);
    const [companyIdFilter, setCompanyIdFilter] = useState(0);
    const [filteredItems, setFilteredItems] = useState<Array<IAutomationItemWithName>>([]);

    // props
    const companies = useSelector<IAppState, Array<IUserCompany>>(state => state.adminCompanies.userCompanies || []);

    const getCompanyName = useCallback(
        row => {
            const company = companies.filter(x => x.CompanyID == row.CompanyId)[0];
            return company ? company.CompanyName : "";
        },
        [companies]
    );

    const filterItems = useCallback(() => {
        const { items } = modalProps;

        let filteredItems = [...items];

        if (companyIdFilter != 0) {
            const filteredCompanies = companies.filter(c => c.CompanyID == companyIdFilter).map(x => x.CompanyID);
            filteredItems = filteredItems.filter(x => filteredCompanies.includes(x.CompanyId));
        }

        if (filteredItems.length > 0) {
            filteredItems = filteredItems.map(x => ({ ...x, CompanyName: getCompanyName(x) }));
        }

        setFilteredItems(filteredItems);
    }, [modalProps, companyIdFilter, companies, getCompanyName]);

    useEffect(() => {
        if (modalProps && modalProps.companyIdFilter) {
            setCompanyIdFilter(modalProps.companyIdFilter);
        }
    }, [modalProps]);

    useEffect(() => {
        if (companies.length > 0 && modalProps && modalProps.items) {
            filterItems();
        }
    }, [companyIdFilter, companies, modalProps, filterItems]);

    const hide = () => {
        resetValues();
        dispatch(hideModal("TEST_AUTOMATION"));
    };

    const resetValues = () => {
        setSelectedValue(5);
        setTable("");
        setSubType(null);
        setCompanyIdFilter(0);
    };

    const handleCompanyFilter = id => {
        setCompanyIdFilter(id);
        setSubType(null);
    };

    const renderInner = () => (
        <div>
            <FormControl>
                Automation Type
                <Select
                    labelId="queuetypes"
                    id="queuetypes"
                    name="queuetypes"
                    value={selectedValue}
                    onChange={e => setSelectedValue(e.target.value as number)}
                >
                    <MenuItem value={3}>Simmons</MenuItem>
                    <MenuItem value={5}>Trigger</MenuItem>
                </Select>
            </FormControl>
            {selectedValue == 5 && (
                <div>
                    <FormControl>
                        <br />
                        Company Filter
                        <Select
                            labelId="subtype"
                            id="subtype"
                            name="subtype"
                            style={{ width: "100%" }}
                            value={companyIdFilter}
                            onChange={e => handleCompanyFilter(e.target.value)}
                        >
                            <MenuItem value={0}>All Companies</MenuItem>
                            {companies != null &&
                                companies.map(x => (
                                    <MenuItem key={x.CompanyID} value={x.CompanyID}>
                                        {x.CompanyName}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <br />
                        Subtype
                        <Select
                            labelId="subtype"
                            id="subtype"
                            name="subtype"
                            style={{ width: "100%" }}
                            value={subType}
                            onChange={e => setSubType(e.target.value as number)}
                        >
                            {filteredItems != null &&
                                filteredItems.map(x => (
                                    <MenuItem key={x.Id} value={x.Id}>
                                        {`${x.JobName} (${x.CompanyName} - ${x.Id})`}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </div>
            )}
            <FormControl>
                <br />
                Table
                <Textfield
                    id="tablename"
                    type="text"
                    value={table}
                    onChange={e => setTable(e.target.value)}
                    inputProps={{
                        maxLength: "256",
                        style: {
                            fontSize: "33px !important",
                        },
                    }}
                    margin="normal"
                />
            </FormControl>
        </div>
    );

    const submitAutomation = () => {
        fetch("/ManageAutomation/UploadTestAutomationItem", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify({
                jobType: selectedValue,
                tableName: table,
                subType,
            }),
        })
            .then(h.checkStatus)
            .then(response => response.json())
            .then(() => {
                dispatch(notifyGreen("Automation test successfully submitted."));
                hide();
            })
            .catch(err => {
                resetValues();
                h.error("Error submitting automation", err);
            });
    };

    return (
        <MaterialModal
            id="test-automation"
            title="Automation Test"
            showPrimaryButton={true}
            isOpen={isOpen}
            onClose={hide}
            cancelButtonText="Close"
            onPrimaryButtonRequest={submitAutomation}
            primaryButtonText={"Add"}
            isPrimaryButtonDisabled={table == "" || (selectedValue == 5 && subType == null)}
        >
            {renderInner()}
        </MaterialModal>
    );
};

export default withModal(TestAutomationModal, "TEST_AUTOMATION");
